/**
 * SceneEvents — Queued event bus for scene-level lifecycle notifications.
 *
 * Events are **queued** when Level/GameObject mutations happen (add/remove
 * objects and components).  A SceneEventsSubsystem flushes the queue once
 * per frame at the start of the update loop, so all subscribers see a
 * consistent, fully-constructed world state.
 *
 * Events:
 *  - objectAdded      — a GameObject was added to the level
 *  - objectRemoved    — a GameObject was removed from the level
 *  - componentAdded   — a component was added to a GameObject
 *  - componentRemoved — a component was removed from a GameObject
 */

import type { GameObject } from './GameObject';
import type { ComponentId } from './types';

// ============================================================================
// Event payloads
// ============================================================================

export interface ObjectEvent {
  gameObject: GameObject;
}

export interface ComponentEvent {
  gameObject: GameObject;
  typeId: string;
  componentId: ComponentId;
}

// ============================================================================
// Listener types
// ============================================================================

export type ObjectListener = (event: ObjectEvent) => void;
export type ComponentListener = (event: ComponentEvent) => void;

// ============================================================================
// Internal queue entry
// ============================================================================

const enum EventKind {
  ObjectAdded,
  ObjectRemoved,
  ComponentAdded,
  ComponentRemoved,
}

interface QueueEntry {
  kind: EventKind;
  gameObject: GameObject;
  typeId?: string;
  componentId?: ComponentId;
}

// ============================================================================
// Subscription types
// ============================================================================

interface ObjectSubscription {
  listener: ObjectListener;
  /** If set, only fire when the GO has at least one of these component types */
  componentFilter?: ReadonlySet<string>;
}

interface ComponentSubscription {
  listener: ComponentListener;
  /** If set, only fire for these component types */
  componentFilter?: ReadonlySet<string>;
}

// ============================================================================
// SceneEvents
// ============================================================================

export class SceneEvents {
  // Subscriptions
  private _objectAdded: ObjectSubscription[] = [];
  private _objectRemoved: ObjectSubscription[] = [];
  private _componentAdded: ComponentSubscription[] = [];
  private _componentRemoved: ComponentSubscription[] = [];

  // Event queue — flushed once per frame by SceneEventsSubsystem
  private _queue: QueueEntry[] = [];
  private _flushing = false;

  /** True when there are queued events waiting to be flushed */
  get dirty(): boolean { return this._queue.length > 0; }

  // --------------------------------------------------------------------------
  // Subscribe
  // --------------------------------------------------------------------------

  /**
   * Subscribe to objectAdded events.
   * @param listener Callback
   * @param componentFilter If provided, only fires when the GO has at least one of these component types
   * @returns Unsubscribe function
   */
  onObjectAdded(listener: ObjectListener, componentFilter?: string[]): () => void {
    const sub: ObjectSubscription = {
      listener,
      componentFilter: componentFilter ? new Set(componentFilter) : undefined,
    };
    this._objectAdded.push(sub);
    return () => { this._objectAdded = this._objectAdded.filter(s => s !== sub); };
  }

  /**
   * Subscribe to objectRemoved events.
   */
  onObjectRemoved(listener: ObjectListener, componentFilter?: string[]): () => void {
    const sub: ObjectSubscription = {
      listener,
      componentFilter: componentFilter ? new Set(componentFilter) : undefined,
    };
    this._objectRemoved.push(sub);
    return () => { this._objectRemoved = this._objectRemoved.filter(s => s !== sub); };
  }

  /**
   * Subscribe to componentAdded events.
   */
  onComponentAdded(listener: ComponentListener, componentFilter?: string[]): () => void {
    const sub: ComponentSubscription = {
      listener,
      componentFilter: componentFilter ? new Set(componentFilter) : undefined,
    };
    this._componentAdded.push(sub);
    return () => { this._componentAdded = this._componentAdded.filter(s => s !== sub); };
  }

  /**
   * Subscribe to componentRemoved events.
   */
  onComponentRemoved(listener: ComponentListener, componentFilter?: string[]): () => void {
    const sub: ComponentSubscription = {
      listener,
      componentFilter: componentFilter ? new Set(componentFilter) : undefined,
    };
    this._componentRemoved.push(sub);
    return () => { this._componentRemoved = this._componentRemoved.filter(s => s !== sub); };
  }

  // --------------------------------------------------------------------------
  // Enqueue  (called by Level / GameObject — never dispatch immediately)
  // --------------------------------------------------------------------------

  /** @internal */
  enqueueObjectAdded(go: GameObject): void {
    this._queue.push({ kind: EventKind.ObjectAdded, gameObject: go });
  }

  /** @internal */
  enqueueObjectRemoved(go: GameObject): void {
    this._queue.push({ kind: EventKind.ObjectRemoved, gameObject: go });
  }

  /** @internal */
  enqueueComponentAdded(go: GameObject, typeId: string, componentId: ComponentId): void {
    this._queue.push({ kind: EventKind.ComponentAdded, gameObject: go, typeId, componentId });
  }

  /** @internal */
  enqueueComponentRemoved(go: GameObject, typeId: string, componentId: ComponentId): void {
    this._queue.push({ kind: EventKind.ComponentRemoved, gameObject: go, typeId, componentId });
  }

  // --------------------------------------------------------------------------
  // Flush  (called once per frame by SceneEventsSubsystem)
  // --------------------------------------------------------------------------

  /**
   * Dispatch all queued events in order.
   * Events enqueued during flush are appended and dispatched in the same pass.
   */
  flush(): void {
    if (this._flushing) return; // prevent reentrant flush
    this._flushing = true;

    // Process queue — new events can be appended during dispatch
    // so we use an index instead of swapping arrays.
    let i = 0;
    while (i < this._queue.length) {
      const entry = this._queue[i++]!;
      switch (entry.kind) {
        case EventKind.ObjectAdded:
          this._dispatchObjectAdded(entry.gameObject);
          break;
        case EventKind.ObjectRemoved:
          this._dispatchObjectRemoved(entry.gameObject);
          break;
        case EventKind.ComponentAdded:
          this._dispatchComponentAdded(entry.gameObject, entry.typeId!, entry.componentId!);
          break;
        case EventKind.ComponentRemoved:
          this._dispatchComponentRemoved(entry.gameObject, entry.typeId!, entry.componentId!);
          break;
      }
    }
    this._queue.length = 0;
    this._flushing = false;
  }

  // --------------------------------------------------------------------------
  // Dispatch helpers (internal)
  // --------------------------------------------------------------------------

  private _dispatchObjectAdded(go: GameObject): void {
    const event: ObjectEvent = { gameObject: go };
    for (const sub of this._objectAdded) {
      if (sub.componentFilter && !matchesAny(go, sub.componentFilter)) continue;
      sub.listener(event);
    }
  }

  private _dispatchObjectRemoved(go: GameObject): void {
    const event: ObjectEvent = { gameObject: go };
    for (const sub of this._objectRemoved) {
      if (sub.componentFilter && !matchesAny(go, sub.componentFilter)) continue;
      sub.listener(event);
    }
  }

  private _dispatchComponentAdded(go: GameObject, typeId: string, componentId: ComponentId): void {
    const event: ComponentEvent = { gameObject: go, typeId, componentId };
    for (const sub of this._componentAdded) {
      if (sub.componentFilter && !sub.componentFilter.has(typeId)) continue;
      sub.listener(event);
    }
  }

  private _dispatchComponentRemoved(go: GameObject, typeId: string, componentId: ComponentId): void {
    const event: ComponentEvent = { gameObject: go, typeId, componentId };
    for (const sub of this._componentRemoved) {
      if (sub.componentFilter && !sub.componentFilter.has(typeId)) continue;
      sub.listener(event);
    }
  }
  /**
   * Discard all pending events without dispatching.
   * Call before play-mode to prevent stale editor events from reaching
   * play-mode subscribers.
   */
  drainQueue(): void {
    this._queue.length = 0;
  }
  // --------------------------------------------------------------------------
  // Cleanup
  // --------------------------------------------------------------------------

  /** Remove all subscriptions and discard pending events */
  clear(): void {
    this._objectAdded = [];
    this._objectRemoved = [];
    this._componentAdded = [];
    this._componentRemoved = [];
    this._queue.length = 0;
    this._flushing = false;
  }
}

// ============================================================================
// Helpers
// ============================================================================

function matchesAny(go: GameObject, filter: ReadonlySet<string>): boolean {
  for (const typeId of go.getComponentTypes()) {
    if (filter.has(typeId)) return true;
  }
  return false;
}
